import { Avatar, SxProps, Theme } from '@mui/material';

/**
 * Palette shared with the app's `src/components/Profile/AccountAvatar.tsx`
 * (`feat/multicuenta-app-switch`, not on `development` yet), in the same order
 * and with the same values: over there they are the `gold5`, `red5`, `primary5`,
 * `green5` and `geekBlue5` tokens of `Styles/variable.ts`.
 *
 * The colour is derived from the id on each client, so web and mobile agree on
 * the colour of a given account only while both derive it from the same list in
 * the same order. Reordering this array repaints every account here and pulls it
 * out of step with the app.
 */
const PALETTE = ['#FFB626', '#FF3F49', '#3E8FFA', '#40D339', '#4760F6'];

/**
 * The colour of an account, derived from its id rather than read from a field:
 * an account has no colour of its own, and all this has to do is make
 * neighbouring rows tell each other apart.
 */
export function colorForAccount(accountId: number) {
  return PALETTE[Math.abs(accountId) % PALETTE.length];
}

const customStyles = {
  circle: {
    color: '#FFFFFF',
    fontWeight: 600,
    flexShrink: 0,
  },
} satisfies Record<string, SxProps<Theme>>;

interface Props {
  accountId: number;
  /** The resolved row label, so an unnamed account still gets a real initial. */
  accountName: string;
  size?: number;
}

export function AccountAvatar({ accountId, accountName, size = 28 }: Props) {
  const initial = accountName?.trim().charAt(0).toUpperCase() || '?';

  return (
    <Avatar
      data-testid={`account-avatar-${accountId}`}
      sx={[
        customStyles.circle,
        {
          width: `${size}px`,
          height: `${size}px`,
          fontSize: `${Math.round(size / 2)}px`,
          backgroundColor: colorForAccount(accountId),
        },
      ]}
    >
      {initial}
    </Avatar>
  );
}
